import { useMutation } from '@tanstack/react-query'
import moment from 'moment/moment'
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { Booking, MAX_HOURLY_DURATION } from '../../models/Booking'
import { ComputeFactory } from '../../models/ComputeFactory'
import { Estimate } from '../../models/Estimate'
import { Industry } from '../../models/Industry'
import { Invoice } from '../../models/Invoice'
import { Item } from '../../models/Item'
import { Job } from '../../models/Job'
import { Lesson } from '../../models/Lesson'
import { Line } from '../../models/Line'
import { PSEvent } from '../../models/PSEvent'
import { Package } from '../../models/Package'
import { PAYEE_TYPE } from '../../models/Payee'
import { RecordDataMap } from '../../models/RecordDataMap'
import { RecordFactory } from '../../models/RecordFactory'
import { Subaccount } from '../../models/Subaccount'
import { Taxcode } from '../../models/Taxcode'
import { User } from '../../models/User'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { Result, okResult } from '../../types/utils'
import { PERM_ROLE_EMPLOYEE } from '../../utils'
import { isPast } from '../../utils/dates'
import { currencyFormat } from '../../utils/numbers'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import { PSApplication } from '../app/PSApplication'
import Button from '../app/design/Button'
import Paragraph from '../app/design/Paragraph'
import { ConfirmationModal } from '../common/modals/ConfirmationModal'
import { WorkflowPage } from '../workflows/WorkflowPage'
import { BookingCompletePage } from './BookingCompletePage'
import { BookingView } from './BookingView'
import { NewBookingPage } from './NewBookingPage'

const bookingInstance = (
    client?: User,
    subaccount?: Subaccount,
    vendor?: User,
    service?: Industry,
    date?: moment.Moment
) => {
    const newBooking = new Booking()
    if (!service && vendor) service = vendor.industry
    if (client) newBooking.client = client
    if (subaccount) newBooking.subaccount = subaccount
    if (vendor) newBooking.setVendorAndOwner(vendor)
    if (service) newBooking.service = service
    if (!!vendor?.line1 && vendor.vendorLocation) {
        newBooking.line1 = vendor.line1
        newBooking.line2 = vendor.line2
        newBooking.city = vendor.city
        newBooking.state = vendor.state
        newBooking.postalCode = vendor.postalCode
    }
    else if (client && client.cityState) {
        newBooking.line1 = client.line1
        newBooking.line2 = client.line2
        newBooking.city = client.city
        newBooking.state = client.state
        newBooking.postalCode = client.postalCode
    }
    if (!newBooking.duration && service) newBooking.duration = service.duration

    // the following line belongs to pre calendar 2.0
    newBooking.date = Booking.nextDate(date || moment(), vendor, PSApplication.me().sp)

    return newBooking
}

const copyBooking = (booking: Booking) => {
    const newBooking = new Booking()
    newBooking.client = booking.client
    newBooking.setVendorAndOwner(booking.vendor)
    newBooking.subaccount = booking.subaccount
    if (newBooking.vendor?.allowStaffing) {
        newBooking.employee = booking.employee
        if (newBooking.employee && newBooking.employee.perm <= PERM_ROLE_EMPLOYEE)
            newBooking.jobs = [Job.initWithPerson(newBooking.employee)]
    }
    newBooking.item = booking.item
    newBooking.lines = []
    for (const line of booking.lines) {
        const lineCopy = Line.initWithLine(line, newBooking)
        newBooking.lines.push(lineCopy)
    }
    newBooking.deposit = booking.deposit
    newBooking.rate = booking.rate
    newBooking.memo = booking.memo
    newBooking.fieldValues = booking.fieldValues
    newBooking.duration = booking.duration
    newBooking.reminder = booking.reminder
    newBooking.channel = booking.channel
    newBooking.line1 = booking.line1
    newBooking.line2 = booking.line2
    newBooking.city = booking.city
    newBooking.state = booking.state
    newBooking.postalCode = booking.postalCode
    newBooking.countryCode = booking.countryCode
    if (newBooking.vendor?.allowContract) newBooking.contract = booking.contract
    newBooking.date = moment()
    if (newBooking.vendor?.allowDiscount) {
        newBooking.discount = booking.discount
        newBooking.discountType = booking.discountType
    }
    if (booking.allowConference) {
        newBooking.allowConference = booking.allowConference
        newBooking.conferenceType = booking.conferenceType
    }
    if (newBooking.vendor?.allowSurcharge) newBooking.allowSurcharge = booking.allowSurcharge
    newBooking.package = undefined
    if (booking.lesson) {
        newBooking.lesson = booking.dropin && !isPast(booking.date) ? booking.lesson : undefined
    }
    else {
        newBooking.date = Booking.nextDate(moment(), newBooking.vendor, PSApplication.me().sp)
        if (newBooking.item && newBooking.item.type === 'reservation')
            newBooking.endDate = moment(newBooking.date).add(
                moment(booking.endDate).diff(moment(booking.date))
            )
    }

    return newBooking
}

const newBooking = async (booking: Booking, logAndDisplayError: (error: any) => void) => {
    if (booking.id) {
        return await initWithBooking(booking)
    }
    else {
        const result = await RecordFactory.getInstance().fetchBooking('load', booking, true)

        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        return await initWithBooking(booking, result.value)
    }
}

const initWithBooking = async (booking: Booking, options?: any) => {
    setDefaultStatus(booking)
    setDefaultChannel(booking)
    setDefaultReminder(booking)
    setDefaultDescription(booking)

    await applyClient(booking)
    applySalesTax(booking)
    applySurcharge(booking)

    if (!booking.item) {
        setDefaultForm(booking)
        setDefaultContract(booking)
    }

    applyLesson(booking, options['lesson'])
    await applyPackage(booking, options['package'])

    return { availablePackages: new RecordDataMap(options['available_packages']) }
}

const setDefaultStatus = (booking: Booking, forVendor = true) => {
    if (!forVendor && !booking.id && !booking.status) booking.status = 'sentRequest'
    else if (forVendor && (booking.status === 'sentRequest' || (!booking.id && !booking.status))) {
        const vendor = booking.vendor
        const status = vendor?.bookingStatus
        const newStatus =
            !booking.deposit && (!status || status === 'confirmed') ? 'confirmed' : 'sentQuote'
        const confirmStatus =
            newStatus === 'confirmed' || status === 'confirmed'
                ? ''
                : !!booking.deposit
                  ? 'confirmPayment'
                  : status
        booking.status = newStatus
        booking.confirmStatus = confirmStatus
    }
}

const setDefaultChannel = (booking: Booking, forVendor = true) => {
    if (forVendor && !booking.id) {
        const client = booking.client
        const vendor = booking.vendor
        let channel = vendor?.channel
        if (vendor?.channel === 'combo') channel = 'combo'
        else if (vendor?.channel === 'sms' && !!client && !client.phone) channel = 'email'
        else if (vendor?.channel === 'email' && !!client && !client.email) channel = 'sms'
        booking.channel = channel
    }
}

const setDefaultReminder = (booking: Booking, forVendor = true) => {
    if (forVendor && !booking.id) {
        const vendor = booking.vendor
        if (vendor?.allowReminder) booking.reminder = vendor.reminder
        else booking.reminder = ''
    }
}

const setDefaultDescription = (booking: Booking) => {
    if (!booking.id) {
        const vendor = booking.vendor
        if (!booking.memo && vendor?.bookingPolicy) booking.memo = vendor.bookingPolicy
    }
}

const setDefaultForm = (booking: Booking) => {
    if (!booking.id) {
        const vendor = booking.vendor
        if (vendor?.bookingForm && !booking.form) booking.form = vendor.bookingForm
    }
}

const setDefaultContract = (booking: Booking) => {
    if (!booking.id) {
        const vendor = booking.vendor
        if (vendor?.bookingContract && !booking.contract) booking.contract = vendor.bookingContract
    }
}

const initBookingForEmployee = (
    booking: Booking,
    employee: User | undefined,
    owner: User,
    me: User
) => {
    if (!owner.allowStaffing) return

    if (employee?.payee && employee.perm <= User.PERM_ROLE_EMPLOYEE) {
        booking.employee =
            employee.perm === User.PERM_ROLE_EMPLOYEE || me.perm >= User.PERM_ROLE_ADMIN
                ? employee
                : undefined
        booking.jobs = [Job.initWithPerson(employee)]
    }
    else if (me.payee && me.perm <= User.PERM_ROLE_EMPLOYEE) {
        booking.employee = me.perm === User.PERM_ROLE_EMPLOYEE ? me : undefined
        booking.jobs = [Job.initWithPerson(me)]
    }
}

const initBookingForEstimate = (booking: Booking, estimate: Estimate | undefined, me: User) => {
    if (!estimate) return

    booking.estimate = estimate
    booking.client = estimate.client
    booking.vendor = estimate.vendor
    booking.subaccount = estimate.subaccount

    if (booking.vendor?.allowStaffing && me.payee && me.perm <= User.PERM_ROLE_EMPLOYEE) {
        booking.employee = me.perm === User.PERM_ROLE_EMPLOYEE ? me : undefined
        booking.jobs = [Job.initWithPerson(me)]
    }
    else if (booking.vendor?.allowStaffing) {
        booking.employee = estimate.employee
    }

    booking.status = 'confirmed'
    booking.date = !!estimate.date ? estimate.date : moment()
    booking.duration = estimate.duration
    booking.memo = estimate.memo
    booking.lines = estimate.lines.filter((line) => !!line.item)
    booking.item = booking.serviceLines.find((line) => !!line.item)?.item

    if (!estimate.vendor?.vendorLocation || booking.item?.clientSelectsLocation) {
        booking.line1 = estimate.client?.line1
        booking.line2 = estimate.client?.line2
        booking.city = estimate.client?.city
        booking.state = estimate.client?.state
        booking.postalCode = estimate.client?.postalCode
    }
}

const initBookingForEvent = (
    booking: Booking,
    event: PSEvent | undefined,
    owner: User,
    me: User
) => {
    if (!event) return

    booking.event = event
    booking.vendor = owner
    booking.date = event.date
    booking.memo = event.notes
    booking.location = event.location

    if (event.duration && event.duration % 15 === 0 && event.duration <= MAX_HOURLY_DURATION) {
        booking.duration = event.duration
    }

    if (booking.vendor?.allowStaffing && me.payee && me.perm <= User.PERM_ROLE_EMPLOYEE) {
        booking.employee = me.perm === User.PERM_ROLE_EMPLOYEE ? me : undefined
        booking.jobs = [Job.initWithPerson(me)]
    }
}

const initBookingForLesson = (booking: Booking, lesson: Lesson | undefined, forDropin: boolean) => {
    if (!lesson?.item) return

    booking.item = lesson.item
    booking.setVendorAndOwner(lesson.item.owner)
    booking.lines = [Line.initWithItem(lesson.item, booking)]
    booking.memo = lesson.item.memo
    booking.deposit = lesson.item.deposit

    booking.line1 = lesson.line1
    booking.line2 = lesson.line2
    booking.city = lesson.city
    booking.state = lesson.state
    booking.postalCode = lesson.postalCode
    booking.countryCode = lesson.countryCode
    booking.duration = lesson.duration
    booking.date = lesson.date

    if (lesson.item.form?.alwaysRequire) {
        booking.form = lesson.item.form
    }

    if (lesson.item.contract?.alwaysRequire) {
        booking.contract = lesson.item.contract
    }

    if (
        lesson.item.owner?.allowDiscount &&
        lesson.item.discount &&
        lesson.item.discountType === 'percent'
    ) {
        booking.discount = lesson.item.discount
        booking.discountType = lesson.item.discountType
    }

    if (forDropin) {
        booking.dropin = true
        booking.lesson = lesson
        // booking.sessions = [booking]
        booking.rate = Math.max((booking.item.rate ?? 0) - (booking.item.deposit ?? 0), 0)
    }
}

const initBookingForPackage = (booking: Booking, aPackage: Package | undefined, me: User) => {
    if (!aPackage) return

    booking.setVendorAndOwner(aPackage.owner)
    booking.client = aPackage.client

    if (booking.vendor?.allowStaffing && me.payee && me.perm <= User.PERM_ROLE_EMPLOYEE) {
        booking.employee = me.perm === User.PERM_ROLE_EMPLOYEE ? me : undefined
        booking.jobs = [Job.initWithPerson(me)]
    }
    else if (booking.vendor?.allowStaffing) {
        booking.employee = aPackage.employee
    }

    booking.line1 = aPackage.item?.line1
    booking.line2 = aPackage.item?.line2
    booking.city = aPackage.item?.city
    booking.state = aPackage.item?.state
    booking.postalCode = aPackage.item?.postalCode
    booking.countryCode = aPackage.item?.countryCode
    booking.memo = aPackage.item?.memo
    booking.date = Booking.nextDate(moment(), booking.vendor, true)
    booking.duration = booking.vendor?.industry?.duration
    booking.confirmStatus = ''
    booking.status = 'confirmed'
}

const initBookingForFixedReservation = (booking: Booking, reservation: Item | undefined) => {
    if (!reservation) return

    booking.item = reservation
    booking.setVendorAndOwner(reservation.owner)
    booking.lines = [Line.initWithItem(reservation, booking)]
    booking.line1 = reservation.line1
    booking.line2 = reservation.line2
    booking.city = reservation.city
    booking.state = reservation.state
    booking.postalCode = reservation.postalCode
    booking.countryCode = reservation.countryCode
    booking.memo = reservation.memo
    booking.duration = reservation.duration
    booking.date = reservation.date
    booking.endDate = reservation.endDate
    booking.deposit = reservation.deposit
    booking.rate = reservation.rate
    booking.form = reservation.form
    booking.contract = reservation.contract
}

const applyClient = async (booking: Booking) => {
    if (!booking.client) return

    const vendor = booking.vendor
    const client = await ComputeFactory.getInstance().fetchClient(vendor, booking.client)

    if (!client) return

    const fields = await RecordFactory.getInstance().loadBookingFields(
        vendor?.id ?? '',
        true,
        false
    )

    for (const field of fields) {
        if (!field.name) continue
        const value = client?.fieldValues.get(field.name)
        if (booking.fieldValues.hasValueForKey(field.name) && !value) {
            delete booking.fieldValues[field.name]
        }
        else {
            booking.fieldValues.put(field.name, value)
        }
    }

    // if (booking.subaccount) {
    //     for (const field of await RecordFactory.getInstance().loadFields(vendor?.id ?? '', undefined, false, true, false, true, false, false, false, true)) {
    //         if (!field.name) continue
    //         const value = client?.fieldValues.get(field.name)
    //         if (booking.subaccount.fieldValues.hasValueForKey(field.name) && !value) {
    //             delete booking.subaccount.fieldValues[field.name]
    //         }
    //         else {
    //             booking.subaccount.fieldValues.put(field.name, value)
    //         }
    //     }
    // }
    if (vendor?.allowDiscount && client?.discount && client.discountType === 'percent') {
        booking.discount = client.discount
        booking.discountType = client.discountType
    }
    if (client?.notify) booking.channel = client.notify
    if (client?.reminder) booking.reminder = client.reminder
    if (booking.channel === 'sms' && !booking.client.phone) {
        booking.channel = 'email'
    }
    else if (booking.channel === 'email' && !booking.client.email) {
        booking.channel = 'sms'
    }
    if (
        (!vendor?.vendorLocation || booking.item?.clientSelectsLocation) &&
        !booking.item?.location &&
        !booking.event?.location
    ) {
        booking.line1 = booking.client?.line1
        booking.line2 = booking.client?.line2
        booking.city = booking.client?.city
        booking.state = booking.client?.state
        booking.postalCode = booking.client?.postalCode
        // if (booking.subaccount)
        //     booking.setAddressFromSubaccount(booking.subaccount)
    }
}

const applySalesTax = (booking: Booking) => {
    const owner = booking.owner
    if (owner && Taxcode.defaultTaxable(owner)) {
        if (Taxcode.defaultOrigin(owner)) {
            booking.nexus = 'pro'
            booking.tax = owner.taxcode?.rate ?? 0
            booking.taxcode = owner.taxcode
        }
        else booking.nexus = 'transaction'
    }
    else {
        booking.nexus = undefined
        booking.tax = 0
        booking.taxcode = undefined
    }
}

const applySurcharge = (booking: Booking) => {
    booking.allowSurcharge = !!booking.vendor?.allowSurcharge
}

const applyLesson = (booking: Booking, lesson: Lesson | undefined) => {
    if (booking.item?.type !== 'class') return

    if (lesson) {
        booking.lessons = booking.dropin ? [lesson] : []
        booking.dates = booking.dropin && lesson.date ? [lesson.date] : []
        booking.date = lesson.date
        booking.duration = lesson.duration
    }
    else {
        booking.lessons = []
        booking.date = undefined
    }
}

export const applyPackage = async (booking: Booking, aPackage: Package | undefined) => {
    if (booking.paidDate || booking.paidDepositDate) return

    if (
        aPackage?.capacityType === 'duration' &&
        ((await aPackage.available()) ?? 0) < (booking.duration ?? 0)
    )
        aPackage = undefined

    if (booking.lines.length > 0) {
        let hasDiscLine = false

        for (const line of booking.lines) {
            line.discountable =
                line.item?.type !== 'product' && (!aPackage || aPackage.item?.appliesTo(line.item!))
            hasDiscLine ||= !!line.discountable
        }

        if (aPackage && !hasDiscLine) aPackage = undefined
    }

    if (aPackage) {
        booking.discount = 100
        booking.discountType = 'percent'

        if (booking.id && booking.package) return // try to preserve existing package

        booking.package = aPackage

        if ((booking.deposit ?? 0) > 0) {
            booking.rate = (booking.rate ?? 0) + (booking.deposit ?? 0)
            booking.deposit = 0
        }
    }
    else if (booking.package) {
        booking.package = undefined
        booking.discount = undefined
        booking.discountType = undefined
    }
    else booking.package = undefined
}

const applyItem = async (booking: Booking) => {
    booking.dropin = false
    booking.lesson = undefined
    booking.dates = []
    booking.lessons = []
    booking.employees = []
    booking.resources = []

    if (booking.item?.isVirtual) {
        booking.line1 = undefined
        booking.line2 = undefined
        booking.city = undefined
        booking.state = undefined
        booking.postalCode = undefined
        booking.countryCode = undefined
    }

    if (booking.item?.type === 'class') {
        booking.schedule = undefined
        booking.forSchedule = false

        if (booking.item?.location) {
            booking.line1 = booking.item.line1
            booking.line2 = booking.item.line2
            booking.city = booking.item.city
            booking.state = booking.item.state
            booking.location = booking.item.location
            booking.postalCode = booking.item.postalCode
            booking.countryCode = booking.item.countryCode
        }
        else if (!booking.item?.isVirtual && booking.vendor?.vendorLocation) {
            booking.line1 = booking.vendor.line1
            booking.line2 = booking.vendor.line2
            booking.city = booking.vendor.city
            booking.state = booking.vendor.state
            booking.postalCode = booking.vendor.postalCode
        }

        booking.jobs = []
        booking.dropin = booking.item.dropin
    }
    else if (booking.item?.type === 'reservation') {
        booking.schedule = undefined
        booking.forSchedule = false

        if (
            booking.item?.location &&
            !booking.event?.location &&
            !booking.item?.clientSelectsLocation
        ) {
            booking.line1 = booking.item.line1
            booking.line2 = booking.item.line2
            booking.city = booking.item.city
            booking.state = booking.item.state
            booking.location = booking.item.location
            booking.postalCode = booking.item.postalCode
            booking.countryCode = booking.item.countryCode
        }
        else if (
            !booking.item?.isVirtual &&
            !booking.item?.clientSelectsLocation &&
            booking.vendor?.vendorLocation
        ) {
            booking.line1 = booking.vendor.line1
            booking.line2 = booking.vendor.line2
            booking.city = booking.vendor.city
            booking.state = booking.vendor.state
            booking.postalCode = booking.vendor.postalCode
        }

        if (booking.item?.duration) booking.duration = booking.item.duration

        if (booking.item?.startDate) {
            booking.endDate = booking.item.endDate
            booking.date = booking.item.startDate
        }
        else {
            const endDate = booking.date!.clone().startOf('day')
            endDate.add(Math.floor((booking.duration ?? 0) / 1440), 'days')

            let checkOutHour = endDate.hour()
            let checkOutMinute = endDate.minute()

            const checkOutTime = booking.item.checkOutTime
            if (checkOutTime !== undefined) {
                checkOutHour = Math.floor(checkOutTime)
                checkOutMinute = checkOutTime % 1 > 0 ? 30 : 0
            }

            booking.endDate = endDate.hour(checkOutHour).minute(checkOutMinute)

            let checkInHour = booking.date!.hour()
            let checkInMinute = booking.date!.minute()

            const checkInTime = booking.item.checkInTime
            if (checkInTime !== undefined) {
                checkInHour = Math.floor(checkInTime)
                checkInMinute = checkInTime % 1 > 0 ? 30 : 0
            }

            booking.date = booking.date!.clone().hour(checkInHour).minute(checkInMinute)
        }
    }
    else {
        if (
            booking.item?.location &&
            !booking.event?.location &&
            !booking.item?.clientSelectsLocation
        ) {
            booking.line1 = booking.item.line1
            booking.line2 = booking.item.line2
            booking.city = booking.item.city
            booking.state = booking.item.state
            booking.postalCode = booking.item.postalCode
            booking.countryCode = booking.item.countryCode
        }
        else if (!booking.item?.isVirtual && !booking.item?.clientSelectsLocation) {
            booking.line1 = booking.vendor?.line1
            booking.line2 = booking.vendor?.line2
            booking.city = booking.vendor?.city
            booking.state = booking.vendor?.state
            booking.postalCode = booking.vendor?.postalCode
        }
    }

    if (
        booking.owner?.allowDiscount &&
        booking.item?.discount &&
        booking.item?.discountType === 'percent'
    ) {
        booking.discount = booking.item.discount
        booking.discountType = booking.item.discountType
    }

    if (
        booking.owner?.allowRecord &&
        booking.item?.form &&
        (!booking.client ||
            (await ComputeFactory.getInstance().requireCapture(booking.client, booking.item.form)))
    ) {
        booking.form = booking.item.form
    }

    if (
        booking.owner?.allowContract &&
        booking.item?.contract &&
        (!booking.client ||
            (await ComputeFactory.getInstance().requireSignature(
                booking.client,
                booking.item.contract
            )))
    ) {
        booking.contract = booking.item.contract
    }

    const notZoomOrZoomEnabled =
        booking.item?.conferenceType !== 'zoom' ||
        (await RecordFactory.getInstance().loadCredentials('zoom')).length > 0

    if (booking.item?.conferenceType && notZoomOrZoomEnabled) {
        booking.conferenceType = booking.item.conferenceType
    }

    if (booking.item?.allowConference) {
        booking.allowConference = true
    }

    if (
        booking.item &&
        (!booking.memo ||
            !booking.vendor?.bookingPolicy ||
            booking.memo !== booking.vendor.bookingPolicy)
    ) {
        booking.memo = booking.item.memo
    }
}

const applyLines = async (booking: Booking) => {
    let duration = 0
    let deposit: number | undefined = 0
    let rate: number | undefined = 0
    let isNull = true

    for (const service of booking.services) {
        deposit += service.type === 'subscription' ? 0 : (service.deposit ?? 0)

        if (
            service.type === 'reservation' &&
            !service.duration &&
            service.rate &&
            (booking.duration ?? 0) >= 1440
        ) {
            rate += (service.rate * (booking.duration ?? 0)) / 1440 - (service.deposit ?? 0)
            duration += booking.duration ?? 0
        }
        else {
            rate +=
                (service.rate ?? 0) - (service.type === 'subscription' ? 0 : (service.deposit ?? 0))
            duration += service.duration ?? 0
        }

        isNull &&= service.rate === undefined
    }

    let deposit2 = 0
    let rate2 = 0

    for (const line of booking.productLines) {
        const product = line.item!
        deposit2 += (line.quantity ?? 1) * (product.deposit ?? 0)
        rate2 += (line.quantity ?? 1) * (product.rate ?? 0 - (product.deposit ?? 0))
        isNull &&= product.rate === undefined
    }

    if (isNull) {
        deposit = rate = undefined
    }
    else {
        rate += rate2
        deposit += deposit2
    }

    for (const opt of Booking.durationStrings(true, false)) {
        if (duration <= Number(opt.id)) {
            duration = Number(opt.id)
            break
        }
    }

    booking.duration = duration

    // don't overwrite deposit amount if already paid
    if (booking.paidDepositDate) {
        if (deposit !== undefined && deposit > (booking.deposit ?? 0)) {
            rate = (rate ?? 0) + deposit - (booking.deposit ?? 0)
            deposit = booking.deposit ?? 0
        }
        else {
            if (deposit !== undefined && deposit < (booking.deposit ?? 0)) {
                alert(
                    `The new deposit ${currencyFormat(deposit)} cannot be less than the paid deposit ${currencyFormat(booking.deposit)}`
                )
                return
            }
        }
        booking.rate = rate
        booking.deposit = deposit
    }
    else if (deposit !== undefined && deposit > 0) {
        booking.rate = rate
        booking.deposit = deposit

        if (!booking.paidDepositDate) {
            booking.confirmStatus = 'confirmPayment'
            booking.status = 'sentQuote'

            if (!booking.channel) {
                booking.channel = !booking.client || booking.client.e164 ? 'sms' : 'email'
            }
        }
    }
    else {
        booking.rate = rate
        booking.deposit = undefined
        if (!booking.id) setDefaultStatus(booking, true)
    }

    // clear out flat rate discounts (or restore client discount) whenever there is a deposit
    // TODO: handle smart campaign discounts as well
    if (
        deposit &&
        booking.discount &&
        !['gift', 'package', 'subscription'].includes(booking.item?.type ?? '')
    ) {
        const client = await ComputeFactory.getInstance().fetchClient(booking.owner, booking.client)
        if (
            client &&
            booking.owner?.allowDiscount &&
            client.discount &&
            client.discountType === 'percent'
        ) {
            booking.discount = client.discount
            booking.discountType = client.discountType
        }
        else {
            booking.discount = undefined
            booking.discountType = undefined
        }
    }

    if (booking.nexus) {
        if (
            booking.nexus === 'transaction' &&
            ['package', 'subscription', 'gift'].includes(booking.item?.type ?? '')
        ) {
            booking.nexus = 'pro'
        }
        const bookingLocation = booking.getLocation()
        if (booking.nexus === 'pro') {
            booking.tax = booking.vendor?.taxcode?.rate ?? 0
            booking.taxcode = booking.vendor?.taxcode
        }
        else if (bookingLocation) {
            const taxcodeResult = await RecordFactory.getInstance().queryTaxcode(bookingLocation)

            let taxcode = taxcodeResult.ok ? taxcodeResult.value : undefined

            if (taxcode?.rate) {
                booking.taxcode = taxcode
                booking.tax = taxcode.rate
                booking.nexus = 'transaction'
            }
            else {
                booking.tax = booking.vendor?.taxcode?.rate ?? 0
                booking.taxcode = booking.vendor?.taxcode
                booking.nexus = 'pro'
            }
        }
        else {
            booking.tax = booking.vendor?.taxcode?.rate ?? 0
            booking.taxcode = booking.vendor?.taxcode
            booking.nexus = 'pro'
        }
    }
    else {
        booking.tax = 0
        booking.taxcode = undefined
    }
}

const viewWillAppear = async ({
    owner,
    me,
    urlParams,
    props: { action },
    searchParams,
    logAndDisplayError,
}: PocketComponent<{ action: 'new' | 'edit' | 'copy' }>) => {
    const [pros, items, zoomCredentials, fields] = await Promise.all([
        RecordFactory.getInstance().loadPayees(PAYEE_TYPE.PAYEE_TYPE_ANY),
        RecordFactory.getInstance().loadItems(owner.id ?? '', [
            'service',
            'class',
            'reservation',
            'product',
        ]),
        RecordFactory.getInstance().loadCredentials('zoom'),
        RecordFactory.getInstance().loadBookingFields(owner.id ?? '', false, false),
    ])

    const { defaultBooking, availablePackages } = await (async (): Promise<{
        defaultBooking: Booking | null
        availablePackages?: RecordDataMap
    }> => {
        switch (action) {
            case 'new': {
                const bookingId = searchParams.get('bookingId')
                const clientId = searchParams.get('clientId')
                const subaccountId = searchParams.get('subaccountId')
                const dateParam = searchParams.get('date')

                const client = clientId
                    ? await RecordFactory.getInstance().loadClient(clientId)
                    : undefined

                const subaccount = subaccountId
                    ? await RecordFactory.getInstance().loadSubaccount(subaccountId)
                    : undefined

                const booking = bookingId
                    ? await RecordFactory.getInstance().loadBooking(bookingId)
                    : bookingInstance(
                          client?.person,
                          subaccount,
                          owner,
                          undefined,
                          dateParam ? moment(dateParam) : moment().startOf('hour')
                      )

                let availablePackages

                if (!booking) {
                    return { defaultBooking: null, availablePackages }
                }

                setDefaultStatus(booking)

                if (!booking.id) {
                    const employeeId = searchParams.get('employeeId')
                    const estimateId = searchParams.get('estimateId')
                    const eventId = searchParams.get('eventId')
                    const lessonId = searchParams.get('lessonId')
                    const packageId = searchParams.get('packageId')
                    const reservationId = searchParams.get('reservationId')

                    if (employeeId) {
                        const employee = await RecordFactory.getInstance().loadUser(employeeId)
                        initBookingForEmployee(booking, employee, owner, me)
                    }

                    if (estimateId) {
                        const estimate = await RecordFactory.getInstance().loadEstimate(estimateId)
                        initBookingForEstimate(booking, estimate, me)
                    }

                    if (eventId) {
                        const event = await RecordFactory.getInstance().loadEvent(eventId)
                        initBookingForEvent(booking, event, owner, me)
                    }

                    if (lessonId) {
                        const lesson = await RecordFactory.getInstance().loadClass(lessonId)
                        const forDropin = searchParams.get('dropin') === 'true'
                        initBookingForLesson(booking, lesson, forDropin)
                    }

                    if (packageId) {
                        const aPackage = await RecordFactory.getInstance().loadPackage(packageId)
                        initBookingForPackage(booking, aPackage, me)
                    }

                    if (reservationId) {
                        const reservation =
                            await RecordFactory.getInstance().loadItem(reservationId)
                        initBookingForFixedReservation(booking, reservation)
                    }

                    availablePackages = (await newBooking(booking, logAndDisplayError))
                        ?.availablePackages
                }

                return { defaultBooking: booking, availablePackages }
            }
            case 'edit': {
                if (!urlParams.id) {
                    return { defaultBooking: null }
                }
                return {
                    defaultBooking:
                        (await RecordFactory.getInstance().loadBooking(urlParams.id)) ?? null,
                }
            }
            case 'copy': {
                if (!urlParams.id) {
                    return { defaultBooking: null }
                }

                const booking =
                    (await RecordFactory.getInstance().loadBooking(urlParams.id)) ?? null

                if (!booking) {
                    return { defaultBooking: null }
                }

                const defaultBooking = copyBooking(booking)
                const availablePackages = (await newBooking(defaultBooking, logAndDisplayError))
                    ?.availablePackages

                return { defaultBooking, availablePackages }
            }
        }
    })()

    if (defaultBooking === null) {
        return new RecordNotFoundError()
    }

    return {
        defaultBooking,
        pros,
        items,
        fields,
        availablePackages,
        zoomConnected: zoomCredentials.length > 0,
    }
}

const NewBookingWorkflowRoot = ({
    data,
    base: { owner, logAndDisplayError },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    const { defaultBooking, pros, items, fields, zoomConnected } = data

    const isEditing = defaultBooking.id !== undefined

    const [searchParams, setSearchParams] = useSearchParams()

    const stepIndex = useMemo(() => {
        switch (searchParams.get('step')) {
            case 'review':
                return 1
            case 'success':
                return 2
            default:
                return 0
        }
    }, [searchParams])

    const [booking, setBooking] = useState(defaultBooking)
    const [availablePackages, setAvailablePackages] = useState(data.availablePackages)
    const [loadingButton, setLoadingButton] = useState<'save' | 'send' | null>(null)

    const onSave = async (notify: boolean) => {
        if (booking.client === undefined) {
            return
        }

        setLoadingButton(notify ? 'send' : 'save')

        let result: Result<Booking | null> = okResult(null)

        if (!booking.id && booking.item?.type === 'class') {
            result = await RecordFactory.getInstance().checkoutClass(booking, !notify)
        }
        else if (!booking.id && booking.dates.length > 1) {
            result = await RecordFactory.getInstance().checkoutBookings(booking, !notify)
        }
        else if (!booking.id) {
            result = await RecordFactory.getInstance().sendBooking(booking, !notify)
        }
        else {
            const changeAll = booking.forSchedule ?? false
            result = await RecordFactory.getInstance().changeBooking(booking, !notify, changeAll)
        }

        setLoadingButton(null)

        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        if (result.value) {
            setBooking(result.value)
        }

        setSearchParams({ step: 'success', notify: notify.toString() }, { replace: true })
    }

    const handleBookingUpdate = async (updatedBooking: Booking, itemId?: string) => {
        if (updatedBooking.hasLineChangesFrom(booking)) {
            await applyLines(updatedBooking)
        }

        if (updatedBooking.client?.id !== booking.client?.id) {
            await syncClientBooking({ booking: updatedBooking })
        }

        if (
            updatedBooking.item?.id !== booking.item?.id ||
            updatedBooking.hasLineChangesFrom(booking)
        ) {
            await syncItemBooking({ booking: updatedBooking, itemId })
        }

        setBooking(updatedBooking)
    }

    const { mutateAsync: syncClientBooking, isLoading: isSyncingClientBooking } = useMutation({
        mutationFn: async ({ booking }: { booking: Booking }) => {
            const result = await RecordFactory.getInstance().fetchBooking('client', booking, true)

            if (!result.ok) throw result.error

            await applyClient(booking)
            await applyPackage(booking, result.value['package'])

            setAvailablePackages(new RecordDataMap(result.value['available_packages']))
        },
        onError: (error) => {
            logAndDisplayError(error)
        },
    })

    const [loadingItemsMap, setLoadingItemsMap] = useState<Record<string, boolean>>({})

    const { mutateAsync: syncItemBooking, isLoading: isSyncingItemBooking } = useMutation({
        mutationFn: async ({ booking, itemId }: { booking: Booking; itemId?: string }) => {
            const result = await RecordFactory.getInstance().fetchBooking('item', booking, true)

            if (!result.ok) throw result.error

            await applyItem(booking)
            applyLesson(booking, result.value['lesson'])
            await applyPackage(booking, result.value['package'])

            setAvailablePackages(new RecordDataMap(result.value['available_packages']))

            return { itemId }
        },
        onMutate: ({ itemId }) => {
            if (!itemId) return
            setLoadingItemsMap((prev) => ({ ...prev, [itemId]: true }))
        },
        onSettled: (data) => {
            if (!data?.itemId) return
            setLoadingItemsMap((prev) => ({ ...prev, [data.itemId as string]: false }))
        },
        onError: (error) => {
            logAndDisplayError(error)
        },
    })

    const isSyncing = isSyncingClientBooking || isSyncingItemBooking
    const canAdvanceToStep2 = !!booking.client && !!booking.date && !isSyncing

    WorkflowPage.useNavigationHandlers({
        stepIndex,
        canAdvanceToStep2,
        isEditing,
        recordID: booking.id,
    })

    const hasUnsavedChanges = stepIndex <= 1 && booking.hasChangesFrom(data.defaultBooking)

    const [guardrail, setGuardrail] = useState<
        | { type: 'validateDeposit'; deposit: number; amount?: number }
        | { type: 'validateTotal' }
        | null
    >(null)

    const handleNext = () => {
        const { deposit, amount, total } = booking

        if (!!deposit && deposit > (amount ?? 0)) {
            setGuardrail({ type: 'validateDeposit', deposit, amount })
            return
        }

        if ((total ?? 0) > Invoice.INVOICE_TOTAL_LIMIT) {
            setGuardrail({ type: 'validateTotal' })
            return
        }

        setSearchParams({ step: 'review' })
    }

    return (
        <>
            <WorkflowPage
                title={stepIndex === 0 ? 'Schedule client' : stepIndex === 1 ? 'Review' : undefined}
                backButtonTitle={
                    stepIndex === 1 ? (isEditing ? 'Edit booking' : 'Schedule client') : undefined
                }
                onBack={() => setSearchParams()}
                steps={[isEditing ? 'Edit' : 'Create', 'Review', 'Send']}
                currentStepIndex={stepIndex}
                shouldForceCurrentStepCheckmark={stepIndex === 2}
                onClose={{
                    action: 'go-back',
                    excludePathname: /^\/bookings.*\/(new|edit|rebook)/,
                    defaultPathname: '/dashboards/bookings',
                }}
                footerContent={
                    stepIndex === 0 || stepIndex === 1 ? (
                        <div className="flex w-full items-center justify-end gap-x-4">
                            {stepIndex === 0 ? (
                                <Button
                                    intent="primary"
                                    disabled={!canAdvanceToStep2}
                                    onClick={handleNext}
                                    isLoading={isSyncing}
                                >
                                    Next
                                </Button>
                            ) : (
                                <>
                                    <Button
                                        intent="secondary"
                                        onClick={() => onSave(false)}
                                        isLoading={loadingButton === 'save'}
                                    >
                                        Save
                                    </Button>

                                    <Button
                                        intent="primary"
                                        onClick={() => onSave(true)}
                                        isLoading={loadingButton === 'send'}
                                    >
                                        Send
                                    </Button>
                                </>
                            )}
                        </div>
                    ) : undefined
                }
                shouldVerticallyCenterContent={stepIndex === 2}
            >
                {stepIndex === 0 ? (
                    <NewBookingPage
                        booking={booking}
                        owner={owner}
                        pros={pros}
                        items={items}
                        fields={fields}
                        zoomConnected={zoomConnected}
                        onUpdateBooking={handleBookingUpdate}
                        loadingItemsMap={loadingItemsMap}
                        isSyncingItemBooking={isSyncingItemBooking}
                        availablePackages={availablePackages}
                    />
                ) : stepIndex === 1 ? (
                    <BookingView booking={booking} customFields={fields} isWorkflowReviewStep />
                ) : stepIndex === 2 ? (
                    <BookingCompletePage booking={booking} />
                ) : null}
            </WorkflowPage>

            <WorkflowPage.NavigationBlocker
                when={hasUnsavedChanges}
                allowNextPaths={[/booking\/.*/]}
            />

            {guardrail?.type === 'validateDeposit' && (
                <ConfirmationModal
                    isOpen
                    title="Please try again"
                    confirmButtonText="OK"
                    onClose={() => setGuardrail(null)}
                    onConfirm={() => setGuardrail(null)}
                    shouldHideCancelButton
                >
                    <Paragraph>
                        The deposit {currencyFormat(guardrail.deposit)} cannot be greater than the
                        net amount {currencyFormat(guardrail.amount ?? 0)}
                    </Paragraph>
                </ConfirmationModal>
            )}

            {guardrail?.type === 'validateTotal' && (
                <ConfirmationModal
                    isOpen
                    title="Exceeded limit"
                    confirmButtonText="OK"
                    onClose={() => setGuardrail(null)}
                    onConfirm={() => setGuardrail(null)}
                    shouldHideCancelButton
                >
                    <Paragraph>
                        The appointment total cannot exceed{' '}
                        {currencyFormat(Invoice.INVOICE_TOTAL_LIMIT)}
                    </Paragraph>
                </ConfirmationModal>
            )}
        </>
    )
}

const render = (props: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    return (
        <WorkflowPage.ResetContainer>
            <NewBookingWorkflowRoot {...props} />
        </WorkflowPage.ResetContainer>
    )
}

export const BookingWorkflow = usePSComponent(render, viewWillAppear)
